"use client";

import { m as motion } from "framer-motion";
import Link from "next/link";
import { useState, type ReactNode } from "react";
import { FaqVisual } from "@/app/[locale]/mvp/FaqVisual";
import {
  Chrome,
  Eye,
  ShieldCheck,
  Lock,
  Gift,
  Layers,
  Building2,
  ChevronDown,
  ArrowRight,
  MousePointerClick,
  FileJson,
  BadgeCheck,
} from "lucide-react";
import { ARGUS_FAQ } from "./argus-faq";

// URL della scheda Chrome Web Store di Argus. Per aggiornarlo senza toccare il
// codice: imposta la env NEXT_PUBLIC_ARGUS_STORE_URL (es. in Coolify o
// .env.local). Altrimenti vale il default qui sotto (URL pubblico dello store).
const STORE_URL =
  process.env.NEXT_PUBLIC_ARGUS_STORE_URL ||
  "https://chromewebstore.google.com/detail/argus-tech-company-inspec/nclgmjdiphdkdffeadhedlaeihcipabd";
const PRIVACY_HREF = "/strumenti/estensioni-chrome/argus/privacy";

const TEAL = "#00D4D4";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5 },
};

const VALUES = [
  {
    icon: Layers,
    title: "Stack completo, con le versioni",
    text: "Riconosce oltre 7.500 tecnologie: CMS, framework, hosting, CDN, librerie JavaScript, e-commerce, analytics e font, con versione e livello di confidenza.",
    keywords: ["oltre 7.500 tecnologie", "versione e livello di confidenza"],
  },
  {
    icon: Building2,
    title: "Chi c'è dietro al dominio",
    text: "Ragione sociale, partita IVA con verifica VIES, indirizzo, email, PEC, telefono e profili social. Dati pubblici, pronti da salvare.",
    keywords: ["partita IVA con verifica VIES", "Dati pubblici"],
  },
  {
    icon: Lock,
    title: "100% locale e privato",
    text: "Funziona tutto sul tuo computer. Nessun account, nessun server, nessun tracciamento. I risultati li vedi solo tu.",
    keywords: ["sul tuo computer", "Nessun account", "nessun server", "nessun tracciamento"],
  },
  {
    icon: Gift,
    title: "Gratis e senza limiti",
    text: "Niente paywall, niente quota mensile di consultazioni. Un'alternativa self-hosted a Wappalyzer, che dal 2023 è a pagamento.",
    keywords: ["Niente paywall", "alternativa self-hosted a Wappalyzer"],
  },
];

// Evidenzia in ciano (TEAL) le keyword dentro un testo: utile per leggibilita'
// e per far emergere i termini anche lato SEO.
const ARGUS_FAQ_KEYWORDS = [
  "oltre 7.500 tecnologie",
  "dati pubblici",
  "partita IVA",
  "in locale",
  "senza limiti",
  "Wappalyzer",
  "tracciamento",
  "abbonamento",
  "sul tuo computer",
  "account",
  "server",
  "gratis",
  "VIES",
];

function highlightTeal(text: string, keywords: string[]): ReactNode {
  if (!keywords.length) return text;
  const escaped = keywords
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "g");
  return text.split(re).map((part, i) =>
    keywords.includes(part) ? (
      <span key={i} className="font-medium" style={{ color: TEAL }}>
        {part}
      </span>
    ) : (
      part
    ),
  );
}

const STEPS = [
  { icon: Eye, title: "Apri un sito", text: "Vai su qualsiasi sito che vuoi analizzare, come faresti normalmente." },
  { icon: MousePointerClick, title: "Clicca Argus", text: "Apri l'estensione dalla barra di Chrome. L'analisi parte subito, in locale." },
  { icon: FileJson, title: "Leggi ed esporta", text: "Vedi stack tecnologico e dati dell'azienda. Esporti tutto in JSON con un clic." },
];

const TECHS = [
  "WordPress", "WooCommerce", "Shopify", "Next.js", "React", "Vue",
  "Cloudflare", "Google Analytics", "Elementor", "Magento", "PHP",
  "jQuery", "Stripe", "HubSpot", "Tailwind", "Webflow", "Laravel", "Nginx",
];

// Icona Argus: occhio stilizzato (ellisse + pupilla ad anello), come i file
// forniti. Se in futuro si vuole il file raster/vettoriale originale, basta
// sostituire questo SVG con un <img src="/brand/argus/..." />.
function EyeMark({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden fill="none">
      <ellipse cx="16" cy="16" rx="13.5" ry="8.5" stroke={TEAL} strokeWidth="1.7" />
      <circle cx="16" cy="16" r="4.6" stroke={TEAL} strokeWidth="2.6" />
    </svg>
  );
}

function StoreButton({
  children,
  large,
  onClick,
}: {
  children: React.ReactNode;
  large?: boolean;
  onClick?: () => void;
}) {
  return (
    <a
      href={STORE_URL}
      target={STORE_URL.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
      onClick={onClick}
      className={`btn-glow inline-flex items-center justify-center gap-2 rounded-xl font-semibold text-[#03131b] transition-transform hover:-translate-y-0.5 ${
        large ? "h-13 px-8 text-base" : "h-12 px-6 text-sm"
      }`}
      style={{ background: TEAL, height: large ? 52 : 48 }}
    >
      <Chrome className="h-5 w-5" />
      {children}
    </a>
  );
}

export function ArgusLanding() {
  return (
    <div style={{ ["--teal" as string]: TEAL } as React.CSSProperties}>
      {/* HERO */}
      <section className="section-padding pt-6 md:pt-8 relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots" />
        <div
          className="absolute -top-32 right-0 h-[520px] w-[520px] rounded-full blur-3xl opacity-25"
          style={{ background: TEAL }}
        />
        <div className="container-wide relative">
          <div className="grid items-center gap-6 sm:gap-12 xl:grid-cols-[1.05fr_0.95fr]">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-[hsl(var(--border))] bg-surface/50 px-4 py-2">
                <EyeMark size={20} />
                <span className="font-mono text-sm font-semibold tracking-wide">Argus</span>
                <span className="text-xs text-muted-foreground">· estensione Chrome</span>
              </div>
              <h1 className="page-hero-title mb-5">
                Scopri con cosa è fatto ogni sito{" "}
                <span className="text-shine">e chi c'è dietro</span>
              </h1>
              <p className="text-body-lg text-muted-foreground mb-8 max-w-xl leading-relaxed">
                Argus è l'estensione Chrome che rivela lo stack tecnologico di qualsiasi sito e i dati
                dell'azienda dietro al dominio.
                <span className="mt-2 block font-semibold" style={{ color: TEAL }}>
                  In locale, gratis, senza limiti.
                </span>
              </p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <StoreButton large>Aggiungi a Chrome</StoreButton>
                <a
                  href="#come-funziona"
                  className="rotating-border inline-flex h-[52px] items-center justify-center gap-2 rounded-xl px-7 font-semibold text-foreground"
                >
                  Come funziona
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
              <p className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" style={{ color: TEAL }} /> Tutto in locale</span>
                <span className="inline-flex items-center gap-1.5"><Gift className="h-3.5 w-3.5" style={{ color: TEAL }} /> Gratis, senza account</span>
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" style={{ color: TEAL }} /> Nessun tracciamento</span>
              </p>
            </motion.div>

            {/* Mock popup */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative mx-auto w-full max-w-md"
            >
              <div className="absolute inset-0 -z-10 rounded-[28px] blur-2xl opacity-20" style={{ background: TEAL }} />
              <MockPopup />
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUE CARDS */}
      <section className="section-padding">
        <div className="container-wide">
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="group surface-card p-6 transition-colors hover:border-primary/40"
              >
                <div className="mb-3 flex items-center gap-3">
                  <span
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                    style={{ background: "rgba(0,212,212,0.12)" }}
                  >
                    <v.icon className="h-5 w-5" style={{ color: TEAL }} />
                  </span>
                  <h3 className="font-display text-base font-semibold transition-colors group-hover:text-[color:var(--teal)]">
                    {v.title}
                  </h3>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {highlightTeal(v.text, v.keywords)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section id="come-funziona" className="section-padding">
        <div className="container-wide">
          <motion.div {...fadeUp} className="mb-10 max-w-2xl">
            <span className="font-mono text-label mb-3 block" style={{ color: TEAL }}>Come funziona</span>
            <h2 className="font-display text-h2 font-bold">Tre clic, e sai tutto del sito</h2>
          </motion.div>
          <div className="grid gap-4 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="group surface-card p-6 transition-colors hover:border-primary/40"
              >
                <div className="mb-3 flex items-center gap-3">
                  <span
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-shadow group-hover:shadow-[0_0_20px_rgba(0,212,212,0.28)]"
                    style={{ background: "rgba(0,212,212,0.12)" }}
                  >
                    <s.icon className="h-5 w-5 text-muted-foreground transition-colors group-hover:text-[color:var(--teal)]" />
                  </span>
                  <h3 className="font-display text-base font-semibold transition-colors group-hover:text-[color:var(--teal)]">
                    {s.title}
                  </h3>
                  <span
                    className="ml-auto font-mono text-sm font-bold"
                    style={{ color: TEAL }}
                  >
                    0{i + 1}
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TECH COVERAGE */}
      <section className="section-padding">
        <div className="container-wide">
          <motion.div {...fadeUp} className="surface-card p-6 md:p-8 xl:p-10">
            <div className="mb-6 max-w-2xl">
              <h2 className="font-display text-2xl md:text-3xl font-bold">Oltre 7.500 tecnologie riconosciute</h2>
              <p className="mt-3 text-muted-foreground">
                Dai CMS più diffusi ai framework moderni, fino a CDN, analytics e sistemi di pagamento.
                Ecco solo alcuni esempi.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {TECHS.map((t) => (
                <span
                  key={t}
                  className="rounded-lg border border-[hsl(var(--border))] bg-surface/50 px-3 py-1.5 text-xs font-mono text-muted-foreground"
                >
                  {t}
                </span>
              ))}
              <span className="rounded-lg px-3 py-1.5 text-xs font-mono" style={{ color: TEAL }}>
                e migliaia altre
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST / PRIVACY */}
      <section className="section-padding">
        <div className="container-wide">
          <motion.div {...fadeUp} className="surface-card p-8 md:p-10">
            <div className="grid gap-8 xl:grid-cols-[1fr_1.1fr] xl:items-center">
              <div>
                <span className="font-mono text-label mb-3 block" style={{ color: TEAL }}>Privacy</span>
                <h2 className="font-display text-2xl md:text-3xl font-bold">
                  I tuoi dati non escono dal tuo computer
                </h2>
                <p className="mt-4 text-muted-foreground leading-relaxed">
                  Argus mostra solo{" "}
                  <span className="font-medium text-foreground">dati già pubblici</span> e li
                  elabora{" "}
                  <span className="font-semibold" style={{ color: TEAL }}>
                    in locale
                  </span>
                  . Le uniche richieste esterne, e solo su tua azione, servono a verificare il
                  dominio (RDAP) e la partita IVA (VIES):{" "}
                  <span className="font-medium text-foreground">
                    nessun dato personale che ti riguarda viene inviato
                  </span>
                  .
                </p>
                <p className="mt-4 text-sm text-muted-foreground/80">
                  È uno strumento di analisi su dati pubblici. Spetta a te usarli nel rispetto delle norme,
                  GDPR incluso, quando contatti aziende o persone.
                </p>
                <Link
                  href={PRIVACY_HREF}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold"
                  style={{ color: TEAL }}
                >
                  Leggi l'informativa privacy
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <ul className="space-y-3">
                {[
                  "Nessun account e nessun server: elaborazione 100% locale",
                  "Nessun sistema di analytics o tracciamento",
                  "Cache dei risultati salvata solo sul tuo dispositivo",
                  "Codice e permessi spiegati in chiaro nell'informativa",
                ].map((item) => (
                  <li key={item} className="grid grid-cols-[20px_minmax(0,1fr)] items-start gap-3 rounded-xl border border-[hsl(var(--border))] bg-surface/40 p-4">
                    <ShieldCheck className="mt-0.5 h-4 w-4" style={{ color: TEAL }} />
                    <span className="text-sm text-foreground/90">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="container-wide">
          <motion.div
            {...fadeUp}
            className="mb-10 max-w-2xl lg:max-w-none text-left"
          >
            <span className="font-mono text-label mb-3 block" style={{ color: TEAL }}>Domande frequenti</span>
            <h2 className="font-display text-h2 font-bold lg:whitespace-nowrap">Tutto quello che ti stai chiedendo</h2>
          </motion.div>

          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-start lg:gap-8">
            <div className="lg:col-span-7">
              <div className="space-y-3">
                {ARGUS_FAQ.map((item) => (
                  <ArgusFaqRow key={item.q} item={item} defaultOpen={false} />
                ))}
              </div>
            </div>
            {/* Companion animato ripreso dall'MVP (bolle domanda/risposta) */}
            <div className="mx-auto h-[20rem] w-full max-w-md lg:col-span-5 lg:mx-0 lg:h-[26rem] lg:max-w-none">
              <FaqVisual isItalian words={["Stack", "Dati", "Locale", "Gratis"]} />
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-padding pb-24 md:pb-32">
        <div className="container-wide">
          <motion.div {...fadeUp} className="surface-card relative max-w-3xl overflow-hidden p-8 md:p-14 text-left">
            <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{ background: TEAL }} />
            <div className="relative max-w-2xl">
              <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: "rgba(0,212,212,0.12)" }}>
                <EyeMark size={30} />
              </div>
              <h2 className="font-display text-2xl md:text-4xl font-bold">Aggiungi Argus a Chrome</h2>
              <p className="mt-4 text-muted-foreground">
                Gratis, in locale, senza account. Scopri lo stack e i contatti di qualsiasi sito in tre clic.
              </p>
              <div className="mt-7 flex justify-start">
                <StoreButton large>Aggiungi a Chrome</StoreButton>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function MockPopup() {
  const [tab, setTab] = useState<"stack" | "azienda">("stack");
  const stack = [
    { name: "WordPress", ver: "6.5", cat: "CMS", conf: 100 },
    { name: "WooCommerce", ver: "8.7", cat: "E-commerce", conf: 100 },
    { name: "Cloudflare", ver: "", cat: "CDN", conf: 98 },
    { name: "Elementor", ver: "3.21", cat: "Page builder", conf: 95 },
    { name: "Google Analytics", ver: "GA4", cat: "Analytics", conf: 90 },
  ];
  return (
    <div className="overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-[#0E1A1D] shadow-[0_30px_70px_rgba(0,0,0,0.5)]">
      <div className="flex items-center gap-2 border-b border-[#1E343B] px-4 py-3">
        <EyeMark size={20} />
        <span className="font-mono text-sm font-semibold text-[#E8F4F4]">Argus</span>
        <span className="ml-auto text-[10px] text-[#7FA6A6]">esempio-sito.it</span>
      </div>
      <div className="flex gap-1 border-b border-[#1E343B] px-3 pt-2">
        {(["stack", "azienda"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className="rounded-t-lg px-3 py-2 text-xs font-medium"
            style={{
              color: tab === t ? TEAL : "#7FA6A6",
              borderBottom: tab === t ? `2px solid ${TEAL}` : "2px solid transparent",
            }}
          >
            {t === "stack" ? "Stack" : "Azienda"}
          </button>
        ))}
      </div>
      <div className="space-y-2 p-3">
        {tab === "stack"
          ? stack.map((s) => (
              <div key={s.name} className="flex items-center gap-3 rounded-lg bg-[#13242A] px-3 py-2.5">
                <span className="font-mono text-xs text-[#E8F4F4]">{s.name}</span>
                {s.ver && <span className="text-[10px] text-[#7FA6A6]">v{s.ver}</span>}
                <span className="ml-auto text-[10px] text-[#7FA6A6]">{s.cat}</span>
                <span className="text-[10px]" style={{ color: TEAL }}>{s.conf}%</span>
              </div>
            ))
          : (
            <div className="space-y-2">
              {[
                ["Ragione sociale", "Esempio S.r.l."],
                ["P. IVA", "01234567890"],
                ["Email", "info@esempio-sito.it"],
                ["PEC", "esempio@pec.it"],
                ["Telefono", "+39 035 000000"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center gap-3 rounded-lg bg-[#13242A] px-3 py-2.5">
                  <span className="text-[10px] uppercase tracking-wide text-[#7FA6A6]">{k}</span>
                  <span className="ml-auto font-mono text-xs text-[#E8F4F4]">{v}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 rounded-lg px-3 py-2.5" style={{ background: "rgba(61,220,151,0.1)" }}>
                <BadgeCheck className="h-4 w-4 text-[#3DDC97]" />
                <span className="text-xs text-[#3DDC97]">Partita IVA valida (verifica VIES)</span>
              </div>
            </div>
          )}
        <button
          type="button"
          className="mt-1 flex w-full items-center justify-center gap-2 rounded-lg border border-[#1E343B] py-2 text-xs font-medium text-[#7FA6A6]"
        >
          <FileJson className="h-3.5 w-3.5" /> Esporta in JSON
        </button>
      </div>
    </div>
  );
}

function ArgusFaqRow({ item, defaultOpen }: { item: { q: string; a: string }; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(Boolean(defaultOpen));

  // Spezza la risposta per frasi, ma NON isola frasi cortissime (es. "Sì.",
  // "No."): le unisce alla frase seguente cosi' non restano su una riga da sole.
  const paragraphs: string[] = [];
  for (const sentence of item.a.split(/(?<=[.!?])\s+(?=[A-ZÀ-Ù])/)) {
    const prev = paragraphs[paragraphs.length - 1];
    const prevWords = prev
      ? prev.replace(/[.!?]/g, "").trim().split(/\s+/).filter(Boolean)
      : [];
    if (prev && prevWords.length <= 1 && prev.length <= 5) {
      paragraphs[paragraphs.length - 1] = `${prev} ${sentence}`;
    } else {
      paragraphs.push(sentence);
    }
  }

  return (
    <div className="surface-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 p-5 text-left"
        aria-expanded={open}
      >
        <span className="font-display text-base font-semibold">{item.q}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: TEAL }} />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        className="overflow-hidden"
      >
        <div className="space-y-2.5 px-5 pb-5">
          {paragraphs.map((paragraph, idx) => (
            <p
              key={idx}
              className="text-sm leading-relaxed text-muted-foreground"
            >
              {highlightTeal(paragraph, ARGUS_FAQ_KEYWORDS)}
            </p>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
